import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { format, parseISO } from 'date-fns'

/**
 * A server instant, on the studio's wall clock.
 *
 * `startTimeLocal` is the only field the server sends already in the studio's own time, and the
 * windows are sent as instants. So the offset is measured between two instants — `startTimeUtc`
 * and whichever edge — and taken off the local start. Formatting `opensAt` directly would put it
 * in the phone's zone, which is the right answer for a member at home in the same city and the
 * wrong one for anybody who has flown; the studio's clock is the one the front desk is reading.
 */
export const onStudioClock = (visit: UpcomingVisit, instant: string) =>
	new Date(
		parseISO(visit.startTimeLocal).getTime() -
			(Date.parse(visit.startTimeUtc) - Date.parse(instant))
	)

/** "12:15 PM", for a window's edge. */
export const clockAt = (visit: UpcomingVisit, instant: string) =>
	format(onStudioClock(visit, instant), 'h:mm a')

/** "12:15", for the opening edge of a range whose closing edge carries the meridiem. */
export const shortClockAt = (visit: UpcomingVisit, instant: string) =>
	format(onStudioClock(visit, instant), 'h:mm')

/** "Fri, Sep 26", for a window that opens days out. */
export const dayAt = (visit: UpcomingVisit, instant: string) =>
	format(onStudioClock(visit, instant), 'EEE, MMM d')

/**
 * "Open from 6:15 to 6:45 PM" — the door's window, in one line, in every state it has.
 *
 * The same sentence before it opens, while it is open and after it has shut. It was going to
 * change tense — "was open" once the window passed — and a line that rewrites itself as the clock
 * moves is a line a member has to re-read; the row's title is already saying which of the three
 * they are in. The opening edge drops its meridiem because the closing one carries it: both edges
 * are inside the same half of the same day, always.
 */
export const windowLine = (visit: UpcomingVisit, opensAt: string, closesAt: string) =>
	`Open from ${shortClockAt(visit, opensAt)} to ${clockAt(visit, closesAt)}`
